/**
 * PhotoContent - Main image display area with overlays
 *
 * Shared component for Edit and View pages.
 * Handles:
 * - Image display (viz or edited)
 * - Loading overlay
 * - Crop mask overlay (when enabled and not in crop mode)
 * - Face overlays (mode-aware: edit vs view)
 * - Stats overlay (optional)
 *
 * Edit-only features (passed as children or render props):
 * - CropOverlay (drag/resize handles)
 * - Develop processing indicators
 */

import { forwardRef, useCallback } from 'react';
import { ImageDisplay } from '../../../edit/components';
import CropMask from '../../../../shared/components/CropMask';
import Spinner from '../../../../shared/components/ui/Spinner';

/**
 * @param {Object} props
 * @param {Object} ref - Ref forwarded to the image element
 *
 * Image sources:
 * @param {string|null} props.currentVizUrl - URL for viz image
 * @param {string|null} props.currentEditedUrl - URL for edited image
 * @param {string|null} [props.vipsProcessedUrl] - URL for wasm-vips processed image
 *
 * Display state:
 * @param {boolean} props.showVizImage - Whether to show viz (vs edited)
 * @param {boolean} [props.showCropMask=true] - Whether to show crop mask
 * @param {boolean} [props.cropMode=false] - Whether in crop mode (hides crop mask)
 * @param {boolean} [props.imageLoading=false] - Whether image is loading
 *
 * Develop mode (Edit-only):
 * @param {boolean} [props.hasAdjustments=false] - Has non-default adjustments
 * @param {boolean} [props.vipsProcessing=false] - wasm-vips is processing
 * @param {boolean} [props.vipsReady=true] - wasm-vips is initialized
 *
 * Crop data:
 * @param {Object|null} props.cropBox - Crop bounds for mask {x1, y1, x2, y2, imgW, imgH}
 *
 * Callbacks:
 * @param {Function} props.onImageLoad - Called when image loads
 * @param {Function} [props.onImageError] - Called on image error
 *
 * Render props (for Edit-only content):
 * @param {Function} [props.renderOverlays] - Render additional overlays (FaceOverlay, CropOverlay)
 */
const PhotoContent = forwardRef(function PhotoContent({
  // Image URLs
  currentVizUrl,
  currentEditedUrl,
  vipsProcessedUrl = null,
  // Display state
  showVizImage,
  showCropMask = true,
  cropMode = false,
  imageLoading = false,
  // Develop mode
  hasAdjustments = false,
  vipsProcessing = false,
  vipsReady = true,
  // Crop data
  cropBox,
  // Callbacks
  onImageLoad,
  onImageError,
  // Render props
  renderOverlays,
  // Refs forwarded via props
  imgRef,
  imgWrapperRef,
}, actualImgRef) {
  // Handler for image load
  const handleImageLoad = useCallback((e) => {
    onImageLoad?.(e);
  }, [onImageLoad]);

  // Handler for image error
  const handleImageError = useCallback((e) => {
    onImageError?.(e);
  }, [onImageError]);

  return (
    <div className="viz-block">
      <div className="viz-frame tall" ref={imgRef}>
        <div className="img-wrapper" ref={imgWrapperRef}>
          {/* Main image display */}
          <ImageDisplay
            ref={actualImgRef}
            vipsProcessedUrl={vipsProcessedUrl}
            currentVizUrl={currentVizUrl}
            currentEditedUrl={currentEditedUrl}
            hasAdjustments={hasAdjustments}
            vipsProcessing={vipsProcessing}
            vipsReady={vipsReady}
            showVizImage={showVizImage}
            cropMode={cropMode}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />

          {/* Loading overlay */}
          {imageLoading && (
            <div className="image-loading-overlay">
              <Spinner size="md" />
            </div>
          )}

          {/* Crop mask - shown when enabled and NOT in crop mode */}
          {showCropMask && !cropMode && cropBox && (
            <CropMask cropBox={cropBox} />
          )}

          {/* Additional overlays (faces, crop handles, stats) via render prop */}
          {renderOverlays?.()}
        </div>
      </div>
    </div>
  );
});

export default PhotoContent;
